import Checkbox from "../checkbox/checkbox.js";
import Icon from "../icon/icon.js";
/**
 * `<Table>` — the flagship data surface. A real `<table>` (native `<thead>/<tbody>/
 * <th scope=col>/<td>` semantics) driven by a **column-def + DataSource** API, authored as a
 * Weave template so it **composes the real child components** (RULE #1) — the selection
 * checkboxes ARE the `<Checkbox>` component (full behaviour, one checkbox in the library, not
 * a restyled native input). Rows are a keyed `@for` over the (sorted) data; arbitrary
 * `Node` cells mount via `@render`. Styled by the Weave design (hairline rows, compact 34px,
 * accent as a mark).
 *
 * Features: sortable headers (`aria-sort`, single active, emit `onSort` + convenience client
 * sort for array sources), row selection (composed `<Checkbox>` column, select-all +
 * indeterminate, accentSoft tint + 2px accent left border) via the CDK `SelectionModel`,
 * expandable detail rows, sticky header + sticky columns (`sticky:'start'|'end'`, offsets from
 * column widths), per-column show/hide (`hidden`), an optional second header row for per-column
 * filters (`headerRow`), an optional `maxHeight` (the body scrolls vertically inside while the
 * header stays), and an optional **virtual body** (`virtual`) that renders only the rows in view.
 *
 *   import Table from '@weave-framework/ui/table';
 *   import Checkbox from '@weave-framework/ui/checkbox';   // optional — the build auto-resolves the composed <Checkbox>
 *   <Table columns={{ cols }} dataSource={{ rows }} selectable expandable />
 */
import { signal, effect, onMount, type Signal, type Computed } from '@weave-framework/runtime';
import {
  selectionModel,
  isDataSource,
  draggable,
  activeDirection,
  resizeSignal,
  virtualScroll,
  type SelectionModel,
  type DataSource,
  type DraggableRef,
  type Size,
  type VirtualScroller,
  type RenderedRange,
} from '../cdk/index.js';

export type SortDirection = 'asc' | 'desc';

export interface TableColumn<T> {
  /** Stable column id + default cell accessor (`row[key]`). */
  key: string;
  /** Header content — text or a node factory. Defaults to `key`. */
  header?: string | (() => Node);
  /** Cell content for a row — text or a node. Defaults to `String(row[key])`. */
  cell?: (row: T) => Node | string;
  /** Make the header a sort button. */
  sortable?: boolean;
  /** Custom comparator for the client-side sort (else value-based, number/string aware). */
  compare?: (a: T, b: T) => number;
  /** Cell text alignment. Numeric columns default to `end`. */
  align?: 'start' | 'center' | 'end';
  /** Right-align + `tabular-nums`. */
  numeric?: boolean;
  /** Freeze the column to an edge while scrolling horizontally (needs a numeric `width`). */
  sticky?: 'start' | 'end';
  /** Hide the column (reactive when `columns` is bound to a signal). */
  hidden?: boolean;
  /** Column width (number → px). Required for a `sticky` column's offset maths. */
  width?: number | string;
  /** Allow dragging this column's header grip to resize it (or set `resizableColumns`). */
  resizable?: boolean;
  /** Minimum width (px) when resizing. Default 48. */
  minWidth?: number;
}

export interface ColumnResize {
  key: string;
  width: number;
}

export interface SortState {
  active: string | null;
  direction: SortDirection | null;
}

export interface TableProps<T = Record<string, unknown>> {
  columns: TableColumn<T>[];
  // A plain getter is accepted, not just a `Signal`: the implementation calls whatever function it
  // is given, and a `computed` (or any `() => T[]`) is the natural thing to hand it. Typed as
  // `Signal<T[]>`, the ordinary reactive case did not type-check.
  dataSource: DataSource<T> | T[] | (() => T[]);
  /** Stable row identity (default: index) — row keys + selection/expansion identity. */
  trackBy?: (row: T) => string | number;

  sort?: SortState;
  onSort?: (sort: SortState) => void;
  disableClear?: boolean;
  /** Disable the convenience client-side sort for array/signal sources. */
  clientSort?: boolean;

  selectable?: boolean;
  selectionMode?: 'single' | 'multiple';
  selection?: SelectionModel<T>;
  onSelectionChange?: (selected: T[]) => void;
  compareWith?: (a: T, b: T) => boolean;

  expandable?: boolean;
  detail?: (row: T) => Node | string;

  /**
   * A second header row, rendered inside `<thead>` directly under the column headers — the slot a
   * per-column filter belongs in. Called once per visible column; return `null` for a column that
   * gets no control.
   *
   * It has to live here rather than above the table, because a row rendered outside `<thead>` only
   * lines up when EVERY column has an explicit width — the moment a column auto-sizes, the two
   * drift apart. Each cell inherits its header's width, alignment and sticky treatment, and sticks
   * under the header when the body scrolls. The synthetic expand/select columns get empty cells.
   */
  headerRow?: (col: TableColumn<T>) => Node | null;

  /** Cap the body height (number → px) — the body scrolls vertically, header stays. */
  maxHeight?: number | string;

  /**
   * Render only the rows in view (plus overscan) instead of the whole page. First render of a large
   * page is the cost this removes: it is linear in cells (~15 µs each — 1000×20 measured at 482 ms
   * of build, 851 ms laid out), and a viewport's worth is 20–40 rows however long the data is.
   * Re-sorting is already cheap without it, because `trackBy` moves existing DOM.
   *
   * Requires `maxHeight` (there is no viewport to window without one) and a UNIFORM row height —
   * the fixed-size strategy maps scroll position to an index arithmetically. `expandable` is
   * therefore not compatible: a detail row of unknown height breaks that mapping. Both are
   * reported at setup rather than left to drift.
   */
  virtual?: boolean;
  /** Row height in px for {@link virtual} — must match what a row actually renders at. Default 34. */
  rowHeight?: number;
  /** Rows rendered above and below the viewport in {@link virtual} mode. Default 6. */
  overscan?: number;

  /** Make every column resizable (per-column `resizable` overrides this). */
  resizableColumns?: boolean;
  /** Controlled column widths (px), keyed by column key — wins over internal + `column.width`. */
  columnWidths?: Record<string, number>;
  /** Called after a resize with the column key + its new width (px). */
  onColumnResize?: (event: ColumnResize) => void;

  ariaLabel?: string;
  emptyText?: string;
  class?: string;
}

// Fixed widths of the synthetic leading columns (used for sticky-offset maths + cell width).
const EXPAND_W: number = 40;
const SELECT_W: number = 44;
const DEFAULT_STICKY_W: number = 120;
const DEFAULT_MIN_W: number = 48;
const RESIZE_STEP: number = 16; // keyboard resize increment (px)
const DEFAULT_ROW_H: number = 34; // the compact row height the design ships (--weave-table-row-height)
const DEFAULT_OVERSCAN: number = 6; // rows kept rendered above + below the viewport

/** A layout slot (synthetic or data column) for sticky-offset computation. */
interface Slot {
  id: string;
  width: number;
  sticky?: 'start' | 'end';
}

/** A pre-resolved cell for a row — built per row so the inner `@for` references only itself
 *  (the compiler names every `@for` item `_row`, so a nested loop can't reach the outer row). */
interface CellView {
  key: string;
  cls: string;
  style: string;
  node: Node;
}

                               
                                                  
                                                                                    
                                                                                                       
                                                                                              
                                                                                                                                                   
                                                                                                                                             
                                                                                                                                                          
            
                                               
                                                                                                          
                                                                                                                   
                                                                                      
                                                                        
                                                   
                                                                                                                 
                                                                            
                                                                                  
                                                                 
            
           
                                                                                                   
                                                                                 
                                                                                                   
                                                                                                                                                                          
                                                                                                                                                                          
                                               
                                                                                                                       
            
              
                                       
                                                                                                    
                                                          
                                                                                                                                                        
                                                      
                                                                                                                                       
                                                                                                                                      
                                                                                              
                                                                                    
                                                          
                                                                                                                                      
                                                                                                                                
                                                    
                                                                               
           
                                                                                                                                                               
       
                                                                                                                                                              
                                                                                                              
                                 

export interface TableContext<T> {
  host: Signal<HTMLElement | null>;
  headEl: Signal<HTMLElement | null>;
  scrollEl: Signal<HTMLElement | null>;
  /** The rows actually rendered — every row, or the virtual window. */
  visibleRows: () => T[];
  topSpacer: () => number;
  bottomSpacer: () => number;
  spacerStyle: (height: number) => string;
  rowStyle: () => string;
  ariaRowCount: () => string | undefined;
  ariaRowIndex: (row: T) => string | undefined;
  hasHeaderRow: () => boolean;
  headerRowNode: (col: TableColumn<T>) => Node;
  headerRowClass: (col: TableColumn<T>) => string;
  /** Append the secondary row's sticky offset (the measured header height) to a cell's style. */
  secondaryStyle: (base: string) => string;
  rootClass: () => string;
  scrollStyle: () => string;
  ariaLabel: () => string | undefined;
  selectable: () => boolean;
  expandable: () => boolean;
  showSelectAll: () => boolean;
  dataCols: () => TableColumn<T>[];
  colSpan: () => number;
  isEmpty: () => boolean;
  emptyText: () => string;
  rows: () => T[];
  rowKey: (row: T) => unknown;
  rowClass: (row: T) => string;
  ariaSelected: (row: T) => 'true' | 'false';
  isSelected: (row: T) => boolean;
  toggleSelect: (row: T, checked: boolean) => void;
  allChecked: () => boolean;
  someChecked: () => boolean;
  onSelectAll: (checked: boolean) => void;
  isExpanded: (row: T) => boolean;
  ariaExpanded: (row: T) => 'true' | 'false';
  rowExpanded: (row: T) => boolean;
  toggleExpand: (row: T) => void;
  headNode: (col: TableColumn<T>) => Node;
  headClass: (col: TableColumn<T>) => string;
  headStyle: (col: TableColumn<T>) => string;
  sortClass: (col: TableColumn<T>) => string;
  ariaSort: (col: TableColumn<T>) => 'ascending' | 'descending' | 'none';
  arrow: (col: TableColumn<T>) => string;
  cycleSort: (key: string) => void;
  isResizable: (col: TableColumn<T>) => boolean;
  resizeLabel: (col: TableColumn<T>) => string;
  gripValueNow: (col: TableColumn<T>) => string | undefined;
  gripValueMin: (col: TableColumn<T>) => string;
  onGripKeydown: (key: string, event: KeyboardEvent) => void;
  cellsFor: (row: T) => CellView[];
  detailNode: (row: T) => Node;
  expandStyle: () => string;
  selectStyle: () => string;
}

export function setup<T = Record<string, unknown>>(props: TableProps<T>): TableContext<T> {
  const host: Signal<HTMLElement | null> = signal<HTMLElement | null>(null);
  const headEl: Signal<HTMLElement | null> = signal<HTMLElement | null>(null);
  const scrollEl: Signal<HTMLElement | null> = signal<HTMLElement | null>(null);

  /* ── data source + client sort ── */
  const rowsView: Computed<T[]> = isDataSource<T>(props.dataSource)
    ? props.dataSource.connect()
    : typeof props.dataSource === 'function'
      ? (props.dataSource as Signal<T[]>)
      : ((): T[] => props.dataSource as T[]);
  const isCustomSource: boolean = isDataSource<T>(props.dataSource);

  const _sort: Signal<SortState> = signal<SortState>(props.sort ?? { active: null, direction: null });
  const sortControlled = (): boolean => props.sort !== undefined;
  const sortState = (): SortState => (sortControlled() ? (props.sort as SortState) : _sort());

  const cmpValues = (a: unknown, b: unknown): number => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b));
  };

  const rows = (): T[] => {
    const base: T[] = rowsView();
    const s: SortState = sortState();
    if (isCustomSource || props.clientSort === false || !s.active || !s.direction) return base;
    const col: TableColumn<T> | undefined = props.columns.find((c) => c.key === s.active);
    if (!col) return base;
    const dir: number = s.direction === 'asc' ? 1 : -1;
    const cmp: (a: T, b: T) => number =
      col.compare ?? ((a, b) => cmpValues((a as Record<string, unknown>)[col.key], (b as Record<string, unknown>)[col.key]));
    return [...base].sort((a, b) => cmp(a, b) * dir);
  };

  /* ── virtual body (fixed-size, on the CDK engine) ── */
  // Reported at setup, not left to drift: without a `maxHeight` the scroll box grows to fit and
  // there is no viewport to window; with `expandable` a detail row of unknown height breaks the
  // arithmetic that maps scroll position to a row index, which would show the WRONG rows rather
  // than merely a wrong scrollbar.
  if (props.virtual && props.maxHeight == null) {
    throw new Error('weave Table: `virtual` needs `maxHeight` — without one the body has no viewport to window.');
  }
  if (props.virtual && props.expandable) {
    throw new Error(
      'weave Table: `virtual` and `expandable` are not compatible — the fixed-size window maps scroll ' +
        'position to a row index, and a detail row of unknown height breaks that mapping. Render the ' +
        'detail in a dialog or a side panel, or drop `virtual`.'
    );
  }
  const rowHeight: number = props.rowHeight ?? DEFAULT_ROW_H;
  const scroller: VirtualScroller | null = props.virtual
    ? virtualScroll({
        itemSize: rowHeight,
        total: (): number => rows().length,
        viewport: (): HTMLElement | null => scrollEl(),
        buffer: props.overscan ?? DEFAULT_OVERSCAN,
      })
    : null;
  const visibleRows = (): T[] => {
    if (!scroller) return rows();
    const { start, end }: RenderedRange = scroller.renderedRange();
    return rows().slice(start, end);
  };

  const cycleSort = (key: string): void => {
    const s: SortState = sortState();
    let next: SortState;
    if (s.active !== key) next = { active: key, direction: 'asc' };
    else if (s.direction === 'asc') next = { active: key, direction: 'desc' };
    else if (s.direction === 'desc')
      next = props.disableClear ? { active: key, direction: 'asc' } : { active: null, direction: null };
    else next = { active: key, direction: 'asc' };
    if (!sortControlled()) _sort.set(next);
    props.onSort?.(next);
  };

  /* ── selection + expansion (CDK SelectionModel) ── */
  const selectable = (): boolean => !!props.selectable;
  const expandable = (): boolean => !!props.expandable;
  const multiple = (): boolean => props.selectionMode !== 'single';
  const selection: SelectionModel<T> =
    props.selection ??
    selectionModel<T>({
      multiple: props.selectionMode !== 'single',
      compareWith: props.compareWith,
      onChange: () => props.onSelectionChange?.(selection.selected()),
    });
  const expanded: SelectionModel<T> = selectionModel<T>({ multiple: true, compareWith: props.compareWith });

  /* ── columns + sticky offsets (from widths — no DOM measurement) ── */
  const dataCols = (): TableColumn<T>[] => props.columns.filter((c) => !c.hidden);

  /* ── column resize: an internal width override (px), keyed by column ── */
  const _widths: Signal<Record<string, number>> = signal<Record<string, number>>({});
  const isResizable = (c: TableColumn<T>): boolean => c.resizable ?? !!props.resizableColumns;
  const minW = (c: TableColumn<T>): number => c.minWidth ?? DEFAULT_MIN_W;
  // Effective px width: controlled prop wins, then an internal resize override, then a numeric `width`.
  const effWidth = (c: TableColumn<T>): number | undefined =>
    props.columnWidths?.[c.key] ?? _widths()[c.key] ?? (typeof c.width === 'number' ? c.width : undefined);
  const setWidth = (c: TableColumn<T>, width: number): void => {
    const w: number = Math.max(minW(c), Math.round(width));
    if (props.columnWidths === undefined) _widths.set({ ..._widths(), [c.key]: w });
    props.onColumnResize?.({ key: c.key, width: w });
  };

  const colW = (c: TableColumn<T>): number => effWidth(c) ?? DEFAULT_STICKY_W;
  const slots = (): Slot[] => {
    const out: Slot[] = [];
    if (expandable()) out.push({ id: '__expand', width: EXPAND_W, sticky: 'start' });
    if (selectable()) out.push({ id: '__select', width: SELECT_W, sticky: 'start' });
    for (const c of dataCols()) out.push({ id: c.key, width: colW(c), sticky: c.sticky });
    return out;
  };
  const stickyStyleFor = (id: string): string => {
    const s: Slot[] = slots();
    const idx: number = s.findIndex((x) => x.id === id);
    if (idx < 0 || !s[idx].sticky) return '';
    // Logical insets so sticky columns dock to the correct edge under RTL.
    if (s[idx].sticky === 'start') {
      let px: number = 0;
      for (let i: number = 0; i < idx; i++) if (s[i].sticky === 'start') px += s[i].width;
      return `inset-inline-start:${px}px`;
    }
    let px: number = 0;
    for (let i: number = s.length - 1; i > idx; i--) if (s[i].sticky === 'end') px += s[i].width;
    return `inset-inline-end:${px}px`;
  };

  const alignOf = (c: TableColumn<T>): string => c.align ?? (c.numeric ? 'end' : 'start');
  const widthCss = (c: TableColumn<T>): string => {
    const eff: number | undefined = effWidth(c);
    if (eff != null) return `width:${eff}px`;
    return c.width == null ? '' : `width:${c.width}`; // a string width passes through
  };
  const joinStyle = (...parts: string[]): string => parts.filter(Boolean).join(';');

  /* ── resize interaction: pointer drag (CDK draggable) + keyboard on the grip ── */
  const resizeCol = (key: string): TableColumn<T> | undefined => props.columns.find((c) => c.key === key);
  let dragStartWidth: number = 0;
  const onGripKeydown = (key: string, event: KeyboardEvent): void => {
    const col: TableColumn<T> | undefined = resizeCol(key);
    if (!col) return;
    const cur: number = effWidth(col) ?? DEFAULT_STICKY_W;
    // In RTL the grip sits on the left edge, so ArrowRight narrows and ArrowLeft widens.
    const grow: number = activeDirection() === 'rtl' ? -RESIZE_STEP : RESIZE_STEP;
    if (event.key === 'ArrowLeft') {
      setWidth(col, cur - grow);
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      setWidth(col, cur + grow);
      event.preventDefault();
    }
  };

  // Attach a pointer-drag to each header grip — RE-ATTACHING whenever the column set changes.
  //
  // This used to be a one-shot `onMount` + `querySelectorAll`, but the header is a keyed `@for` over the
  // reactive `dataCols()`: unhiding a column, appending one, or flipping `resizable` creates a new grip
  // element that the single startup pass never saw. Keyboard resize kept working (it is an `on:keydown`
  // template binding, re-applied per element) while pointer drag silently did not — a half-working control
  // with no visible difference. And if `columns` arrived asynchronously, the mount pass matched nothing at
  // all and NO column ever got drag.
  //
  // Reading `dataCols()` makes this effect re-run on any column change; previous refs are destroyed first
  // so grips that survived the re-render do not accumulate duplicate listeners.
  let gripRefs: DraggableRef[] = [];
  onMount(() => {
    const el: HTMLElement | null = host();
    if (!el) return;
    // The effect is created INSIDE onMount so its first run already has the rendered header to query; it
    // then re-runs on every `dataCols()` change. Creating it at setup time instead would fire once against
    // an empty (or absent) DOM and attach nothing.
    effect(() => {
      dataCols(); // dependency: re-attach whenever the column set changes
      for (const ref of gripRefs) ref.destroy();
      gripRefs = [];
      el.querySelectorAll<HTMLElement>('.weave-table__resize-grip').forEach((grip) => {
        const key: string = grip.getAttribute('data-col') ?? '';
        const col: TableColumn<T> | undefined = resizeCol(key);
        if (!col) return;
        gripRefs.push(
          draggable(grip, {
            axis: 'x',
            onStart: () => {
              dragStartWidth = effWidth(col) ?? (grip.closest('th') as HTMLElement | null)?.offsetWidth ?? DEFAULT_STICKY_W;
              el.setAttribute('data-resizing', 'true');
            },
            onMove: ({ dx }) => setWidth(col, dragStartWidth + dx),
            onEnd: () => el.removeAttribute('data-resizing'),
          })
        );
      });
    });
  });

  // Key rows by a stable id (trackBy) or by object identity — NOT by index, so a sort
  // reorders the existing DOM by identity (content stays with its row) instead of rebinding
  // positions (which, with one-shot `@render` cells, would strand stale content).
  const rowKey = (row: T): unknown => (props.trackBy ? String(props.trackBy(row)) : row);

  const asNode = (content: Node | string): Node => (typeof content === 'string' ? document.createTextNode(content) : content);

  /* ── secondary header row (per-column filters) ── */
  // Both header rows are `position: sticky`. The first pins at 0; the second has to pin at the
  // first one's height, which CSS cannot express — a header can wrap, carry a sort button, or be
  // resized. So measure it (ResizeObserver-backed, so it re-pins when the header changes height)
  // and write the offset inline. Before the ref lands the offset is 0, which is only ever wrong
  // for the frame before the observer reports.
  const hasHeaderRow = (): boolean => props.headerRow !== undefined;
  let headSize: (() => Size) | null = null;
  const headerHeight = (): number => {
    const el: HTMLElement | null = headEl();
    if (!el) return 0;
    if (!headSize) headSize = resizeSignal(el);
    return Math.round(headSize().height);
  };

  const cellClassOf = (col: TableColumn<T>): string => {
    const parts: string[] = ['weave-table__cell'];
    if (col.numeric) parts.push('weave-table__cell--numeric');
    if (col.sticky) parts.push(`weave-table__cell--sticky-${col.sticky}`);
    return parts.join(' ');
  };
  const cellStyleOf = (col: TableColumn<T>): string => joinStyle(widthCss(col), `text-align:${alignOf(col)}`, stickyStyleFor(col.key));
  const cellNodeOf = (row: T, col: TableColumn<T>): Node =>
    asNode(col.cell ? col.cell(row) : String((row as Record<string, unknown>)[col.key] ?? ''));

  return {
    host,
    headEl,
    scrollEl,
    visibleRows,
    topSpacer: (): number => (scroller ? Math.round(scroller.scrollOffset()) : 0),
    bottomSpacer: (): number => (scroller ? Math.max(0, Math.round(scroller.endOffset())) : 0),
    spacerStyle: (height: number): string => `height:${height}px;padding:0;box-shadow:none`,
    // A virtual row must render at exactly `rowHeight`, since that is what the window arithmetic
    // assumes. Outside virtual mode the row keeps its natural height.
    rowStyle: (): string => (scroller ? `height:${rowHeight}px` : ''),
    // Only a windowed grid needs these: a reader sees a handful of rows and has to be told how
    // many there really are, and which one each rendered row is. `aria-rowcount` counts the header
    // row too, and `aria-rowindex` is 1-based with the header at 1.
    ariaRowCount: (): string | undefined => (scroller ? String(rows().length + 1) : undefined),
    ariaRowIndex: (row: T): string | undefined => {
      if (!scroller) return undefined;
      const i: number = rows().indexOf(row);
      return i < 0 ? undefined : String(i + 2);
    },
    hasHeaderRow,
    headerRowNode: (col: TableColumn<T>): Node => asNode(props.headerRow?.(col) ?? ''),
    headerRowClass: (col: TableColumn<T>): string => {
      const parts: string[] = ['weave-table__header-filter-cell'];
      if (col.sticky) parts.push(`weave-table__cell--sticky-${col.sticky}`);
      return parts.join(' ');
    },
    secondaryStyle: (base: string): string => joinStyle(base, `top:${headerHeight()}px`),
    rootClass: (): string => {
      const parts: string[] = ['weave-table'];
      if (selectable()) parts.push('weave-table--selectable');
      if (props.class) parts.push(props.class);
      return parts.join(' ');
    },
    scrollStyle: (): string =>
      props.maxHeight == null ? '' : `max-height:${typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight}`,
    ariaLabel: (): string | undefined => props.ariaLabel,
    selectable,
    expandable,
    showSelectAll: (): boolean => multiple(),
    dataCols,
    colSpan: (): number => dataCols().length + (selectable() ? 1 : 0) + (expandable() ? 1 : 0),
    isEmpty: (): boolean => rows().length === 0,
    emptyText: (): string => props.emptyText ?? 'No data',
    rows,
    rowKey,
    rowClass: (row: T): string => (expanded.isSelected(row) ? 'weave-table__row weave-table__row--expanded' : 'weave-table__row'),
    ariaSelected: (row: T): 'true' | 'false' => (selection.isSelected(row) ? 'true' : 'false'),
    isSelected: (row: T): boolean => selection.isSelected(row),
    // The composed <Checkbox>'s onChange delivers the next checked state as a boolean (only
    // real `on:X` events are auto-forwarded, so a data-callback prop fires exactly once).
    toggleSelect: (row: T, checked: boolean): void => {
      if (checked) selection.select(row);
      else selection.deselect(row);
    },
    allChecked: (): boolean => {
      const data: T[] = rows();
      return data.length > 0 && data.every((r) => selection.isSelected(r));
    },
    someChecked: (): boolean => {
      const data: T[] = rows();
      const n: number = data.filter((r) => selection.isSelected(r)).length;
      return n > 0 && n < data.length;
    },
    onSelectAll: (checked: boolean): void => {
      if (checked) selection.setSelection(...rows());
      else selection.clear();
    },
    isExpanded: (row: T): boolean => expanded.isSelected(row),
    ariaExpanded: (row: T): 'true' | 'false' => (expanded.isSelected(row) ? 'true' : 'false'),
    rowExpanded: (row: T): boolean => expandable() && expanded.isSelected(row),
    toggleExpand: (row: T): void => expanded.toggle(row),
    headNode: (col: TableColumn<T>): Node =>
      typeof col.header === 'function' ? col.header() : document.createTextNode(col.header ?? col.key),
    headClass: (col: TableColumn<T>): string =>
      col.sticky ? `weave-table__header-cell weave-table__cell--sticky-${col.sticky}` : 'weave-table__header-cell',
    headStyle: (col: TableColumn<T>): string => joinStyle(widthCss(col), `text-align:${alignOf(col)}`, stickyStyleFor(col.key)),
    sortClass: (col: TableColumn<T>): string =>
      sortState().active === col.key ? 'weave-table__sort weave-table__sort--active' : 'weave-table__sort',
    ariaSort: (col: TableColumn<T>): 'ascending' | 'descending' | 'none' => {
      const s: SortState = sortState();
      if (s.active !== col.key || !s.direction) return 'none';
      return s.direction === 'asc' ? 'ascending' : 'descending';
    },
    // The sort indicator is a lucide icon NAME (not a ↑/↓ character) — '' when this column is
    // not the active sort, which the template uses as the render condition.
    arrow: (col: TableColumn<T>): string => {
      const s: SortState = sortState();
      if (s.active !== col.key || !s.direction) return '';
      return s.direction === 'desc' ? 'arrow-down' : 'arrow-up';
    },
    cycleSort,
    isResizable,
    resizeLabel: (col: TableColumn<T>): string =>
      `Resize ${typeof col.header === 'string' ? col.header : col.key} column`,
    // APG window-splitter: the separator exposes its current width (once known — reactive on resize)
    // and the min it can shrink to. No hard max, so aria-valuemax is intentionally omitted.
    gripValueNow: (col: TableColumn<T>): string | undefined => {
      const w: number | undefined = effWidth(col);
      return w != null ? String(Math.round(w)) : undefined;
    },
    gripValueMin: (col: TableColumn<T>): string => String(minW(col)),
    onGripKeydown,
    cellsFor: (row: T): CellView[] =>
      dataCols().map((col) => ({ key: col.key, cls: cellClassOf(col), style: cellStyleOf(col), node: cellNodeOf(row, col) })),
    detailNode: (row: T): Node => asNode(props.detail ? props.detail(row) : ''),
    expandStyle: (): string => joinStyle('width:' + EXPAND_W + 'px', stickyStyleFor('__expand')),
    selectStyle: (): string => joinStyle('width:' + SELECT_W + 'px', stickyStyleFor('__select')),
  };
}

import { defineComponent } from "@weave-framework/runtime/dom";

import { bindAttr, bindText, child, clone, cloneFragment, eachBlock, ifBlock, listen, mountChild, setRef, template } from "@weave-framework/runtime/dom";

const _t0 = template("<th data-w-vn5qgp class=\"weave-table__header-cell weave-table__expand weave-table__cell--sticky-start\"><\/th>");
const _t1 = template("<!---->");
const _t2 = template("<th data-w-vn5qgp class=\"weave-table__header-cell weave-table__select weave-table__cell--sticky-start\"><!----><\/th>");
const _t3 = template("<!---->");
const _t4 = template("<button data-w-vn5qgp type=\"button\"><!----><span data-w-vn5qgp class=\"weave-table__sort-arrow\" aria-hidden=\"true\"><!----><\/span><\/button>");
const _t5 = template("<!---->");
const _t6 = template("<span data-w-vn5qgp class=\"weave-table__resize-grip\" role=\"separator\" aria-orientation=\"vertical\" tabindex=\"0\"><\/span>");
const _t7 = template("<th data-w-vn5qgp scope=\"col\"><!----><!----><!----><\/th>");
const _t8 = template("<td data-w-vn5qgp class=\"weave-table__header-filter-cell weave-table__expand weave-table__cell--sticky-start\"><\/td>");
const _t9 = template("<td data-w-vn5qgp class=\"weave-table__header-filter-cell weave-table__select weave-table__cell--sticky-start\"><\/td>");
const _t10 = template("<td data-w-vn5qgp><!----><\/td>");
const _t11 = template("<tr data-w-vn5qgp class=\"weave-table__header-row weave-table__header-row--secondary\"><!----><!----><!----><\/tr>");
const _t12 = template("<tr data-w-vn5qgp class=\"weave-table__spacer\" aria-hidden=\"true\"><td data-w-vn5qgp><\/td><\/tr>");
const _t13 = template("<td data-w-vn5qgp class=\"weave-table__cell weave-table__expand weave-table__cell--sticky-start\"><button data-w-vn5qgp type=\"button\" class=\"weave-table__expand-toggle\" aria-label=\"Toggle row details\"><!----><\/button><\/td>");
const _t14 = template("<td data-w-vn5qgp class=\"weave-table__cell weave-table__select weave-table__cell--sticky-start\"><!----><\/td>");
const _t15 = template("<td data-w-vn5qgp><!----><\/td>");
const _t16 = template("<tr data-w-vn5qgp class=\"weave-table__detail\"><td data-w-vn5qgp class=\"weave-table__detail-cell\"><!----><\/td><\/tr>");
const _t17 = template("<tr data-w-vn5qgp><!----><!----><!----><\/tr><!---->");
const _t18 = template("<tr data-w-vn5qgp class=\"weave-table__spacer\" aria-hidden=\"true\"><td data-w-vn5qgp><\/td><\/tr>");
const _t19 = template("<tr data-w-vn5qgp><td data-w-vn5qgp class=\"weave-table__empty\"><!----><\/td><\/tr>");
const _t20 = template("<div data-w-vn5qgp><div data-w-vn5qgp class=\"weave-table__scroll\"><table data-w-vn5qgp class=\"weave-table__grid\"><thead data-w-vn5qgp class=\"weave-table__header\"><tr data-w-vn5qgp class=\"weave-table__header-row\"><!----><!----><!----><\/tr><!----><\/thead><tbody data-w-vn5qgp class=\"weave-table__body\"><!----><!----><!----><!----><\/tbody><\/table><\/div><\/div>");
function render(ctx, slots) {
  const _r = clone(_t20);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 0, 0);
  const _n2 = child(_r, 0, 0, 0, 0);
  const _n3 = child(_r, 0, 0, 0, 0, 0);
  const _n4 = child(_r, 0, 0, 0, 0, 1);
  const _n5 = child(_r, 0, 0, 0, 0, 2);
  const _n6 = child(_r, 0, 0, 0, 1);
  const _n7 = child(_r, 0, 0, 1, 0);
  const _n8 = child(_r, 0, 0, 1, 1);
  const _n9 = child(_r, 0, 0, 1, 2);
  const _n10 = child(_r, 0, 0, 1, 3);
  bindAttr(_r, "class", () => (ctx.rootClass()));
  setRef(ctx.host, _r);
  bindAttr(_n0, "style", () => (ctx.scrollStyle()));
  setRef(ctx.scrollEl, _n0);
  bindAttr(_n1, "aria-label", () => (ctx.ariaLabel()));
  bindAttr(_n1, "aria-rowcount", () => (ctx.ariaRowCount()));
  setRef(ctx.headEl, _n2);
  ifBlock(_n3, () => { if (ctx.expandable()) return _b0; return null; })
  ifBlock(_n4, () => { if (ctx.selectable()) return _b1; return null; })
  eachBlock(_n5, () => ctx.dataCols(), (col, $index) => col.key, _b3)
  ifBlock(_n6, () => { if (ctx.hasHeaderRow()) return _b8; return null; })
  ifBlock(_n7, () => { if (ctx.topSpacer()) return _b12; return null; })
  eachBlock(_n8, () => ctx.visibleRows(), (row, $index) => ctx.rowKey(row), _b13)
  ifBlock(_n9, () => { if (ctx.bottomSpacer()) return _b18; return null; })
  ifBlock(_n10, () => { if (ctx.isEmpty()) return _b19; return null; })
  return _r;
  function _b0() {
  const _r = clone(_t0);
  bindAttr(_r, "style", () => (ctx.expandStyle()));
  return _r;
}
  function _b1() {
  const _r = clone(_t2);
  const _n0 = child(_r, 0);
  bindAttr(_r, "style", () => (ctx.selectStyle()));
  ifBlock(_n0, () => { if (ctx.showSelectAll()) return _b2; return null; })
  return _r;
  function _b2() {
  const _r = cloneFragment(_t1);
  const _n0 = child(_r, 0);
  mountChild(_n0, Checkbox({ get checked() { return ctx.allChecked(); }, get indeterminate() { return ctx.someChecked(); }, get onChange() { return ctx.onSelectAll; }, label: "Select all rows" }, {}));
  return _r;
}
}
  function _b3(_row0) {
  const _r = clone(_t7);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 1);
  const _n2 = child(_r, 2);
  bindAttr(_r, "class", () => (ctx.headClass(_row0.item())));
  bindAttr(_r, "style", () => (ctx.headStyle(_row0.item())));
  bindAttr(_r, "aria-sort", () => (ctx.ariaSort(_row0.item())));
  ifBlock(_n0, () => { if (_row0.item().sortable) return _b4; return null; })
  ifBlock(_n1, () => { if (!_row0.item().sortable) return _b6; return null; })
  ifBlock(_n2, () => { if (ctx.isResizable(_row0.item())) return _b7; return null; })
  return _r;
  function _b4() {
  const _r = clone(_t4);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 1, 0);
  bindAttr(_r, "class", () => (ctx.sortClass(_row0.item())));
  listen(_r, "click", () => ctx.cycleSort(_row0.item().key));
  mountChild(_n0, ctx.headNode(_row0.item()));
  ifBlock(_n1, () => { if (ctx.arrow(_row0.item())) return _b5; return null; })
  return _r;
  function _b5() {
  const _r = cloneFragment(_t3);
  const _n0 = child(_r, 0);
  mountChild(_n0, Icon({ get name() { return ctx.arrow(_row0.item()); } }, {}));
  return _r;
}
}
  function _b6() {
  const _r = cloneFragment(_t5);
  const _n0 = child(_r, 0);
  mountChild(_n0, ctx.headNode(_row0.item()));
  return _r;
}
  function _b7() {
  const _r = clone(_t6);
  bindAttr(_r, "aria-label", () => (ctx.resizeLabel(_row0.item())));
  bindAttr(_r, "data-col", () => (_row0.item().key));
  bindAttr(_r, "aria-valuenow", () => (ctx.gripValueNow(_row0.item())));
  bindAttr(_r, "aria-valuemin", () => (ctx.gripValueMin(_row0.item())));
  listen(_r, "keydown", (e) => ctx.onGripKeydown(_row0.item().key, e));
  return _r;
}
}
  function _b8() {
  const _r = clone(_t11);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 1);
  const _n2 = child(_r, 2);
  ifBlock(_n0, () => { if (ctx.expandable()) return _b9; return null; })
  ifBlock(_n1, () => { if (ctx.selectable()) return _b10; return null; })
  eachBlock(_n2, () => ctx.dataCols(), (col, $index) => col.key, _b11)
  return _r;
  function _b9() {
  const _r = clone(_t8);
  bindAttr(_r, "style", () => (ctx.secondaryStyle(ctx.expandStyle())));
  return _r;
}
  function _b10() {
  const _r = clone(_t9);
  bindAttr(_r, "style", () => (ctx.secondaryStyle(ctx.selectStyle())));
  return _r;
}
  function _b11(_row1) {
  const _r = clone(_t10);
  const _n0 = child(_r, 0);
  bindAttr(_r, "class", () => (ctx.headerRowClass(_row1.item())));
  bindAttr(_r, "style", () => (ctx.secondaryStyle(ctx.headStyle(_row1.item()))));
  mountChild(_n0, ctx.headerRowNode(_row1.item()));
  return _r;
}
}
  function _b12() {
  const _r = clone(_t12);
  const _n0 = child(_r, 0);
  bindAttr(_n0, "colspan", () => (ctx.colSpan()));
  bindAttr(_n0, "style", () => (ctx.spacerStyle(ctx.topSpacer())));
  return _r;
}
  function _b13(_row2) {
  const _r = cloneFragment(_t17);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 0, 0);
  const _n2 = child(_r, 0, 1);
  const _n3 = child(_r, 0, 2);
  const _n4 = child(_r, 1);
  bindAttr(_n0, "class", () => (ctx.rowClass(_row2.item())));
  bindAttr(_n0, "aria-selected", () => (ctx.ariaSelected(_row2.item())));
  bindAttr(_n0, "aria-rowindex", () => (ctx.ariaRowIndex(_row2.item())));
  bindAttr(_n0, "style", () => (ctx.rowStyle()));
  ifBlock(_n1, () => { if (ctx.expandable()) return _b14; return null; })
  ifBlock(_n2, () => { if (ctx.selectable()) return _b15; return null; })
  eachBlock(_n3, () => ctx.cellsFor(_row2.item()), (cell, $index) => cell.key, _b16)
  ifBlock(_n4, () => { if (ctx.rowExpanded(_row2.item())) return _b17; return null; })
  return _r;
  function _b14() {
  const _r = clone(_t13);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 0, 0);
  bindAttr(_r, "style", () => (ctx.expandStyle()));
  bindAttr(_n0, "aria-expanded", () => (ctx.ariaExpanded(_row2.item())));
  listen(_n0, "click", () => ctx.toggleExpand(_row2.item()));
  mountChild(_n1, Icon({ get name() { return 'chevron-right'; } }, {}));
  return _r;
}
  function _b15() {
  const _r = clone(_t14);
  const _n0 = child(_r, 0);
  bindAttr(_r, "style", () => (ctx.selectStyle()));
  mountChild(_n0, Checkbox({ get checked() { return ctx.isSelected(_row2.item()); }, get onChange() { return (checked) => ctx.toggleSelect(_row2.item(), checked); }, label: "Select row" }, {}));
  return _r;
}
  function _b16(_row3) {
  const _r = clone(_t15);
  const _n0 = child(_r, 0);
  bindAttr(_r, "class", () => (_row3.item().cls));
  bindAttr(_r, "style", () => (_row3.item().style));
  mountChild(_n0, _row3.item().node);
  return _r;
}
  function _b17() {
  const _r = clone(_t16);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 0, 0);
  bindAttr(_n0, "colspan", () => (ctx.colSpan()));
  mountChild(_n1, ctx.detailNode(_row2.item()));
  return _r;
}
}
  function _b18() {
  const _r = clone(_t18);
  const _n0 = child(_r, 0);
  bindAttr(_n0, "colspan", () => (ctx.colSpan()));
  bindAttr(_n0, "style", () => (ctx.spacerStyle(ctx.bottomSpacer())));
  return _r;
}
  function _b19() {
  const _r = clone(_t19);
  const _n0 = child(_r, 0);
  const _n1 = child(_r, 0, 0);
  bindAttr(_n0, "colspan", () => (ctx.colSpan()));
  bindText(_n1, () => (ctx.emptyText()));
  return _r;
}
}

export default defineComponent(render, setup);